import { Link } from 'react-router-dom';
import { HeartIcon, MapPinIcon, PhoneIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import LogoImage from './shared/LogoImage';
import { AdminAccessLink } from './admin/AdminAccessLink';
import { useConsent } from '@/contexts/CookieConsentContext';
const Footer = () => {
  const { openSettings } = useConsent();
  return <footer className="bg-[var(--gray-50)] mt-32 relative overflow-hidden shadow-flat">
      
      <div className="container mx-auto px-4 py-16 relative">
        <div className="grid md:grid-cols-4 gap-12">
          {/* Enhanced Company Info */}
          <div className="space-y-6">
            <Link to="/" className="flex items-center group">
              <LogoImage />
            </Link>
            <p className="text-primary font-bold text-lg mb-2">
              Independence at home. Strength together.
            </p>
            <p className="text-muted-foreground leading-relaxed text-sm">
              Helping older adults stay independent, safe, and supported at home 
              while giving families clarity, confidence, and peace of mind.
            </p>
          </div>

          {/* Enhanced Quick Links */}
          <div>
            <h3 className="font-bold text-foreground mb-6 text-lg">Quick Links</h3>
            <div className="space-y-3">
              <Link to="/about-seniorthrive" className="text-muted-foreground hover:text-primary transition-colors duration-300 block font-bold">
                About Us
              </Link>
              <Link to="/support/faq" className="text-muted-foreground hover:text-primary transition-colors duration-300 block font-bold">
                FAQs & Support
              </Link>
              <Link to="/pricing" className="text-muted-foreground hover:text-primary transition-colors duration-300 block font-bold">
                Pricing
              </Link>
              <Link to="/contact" className="text-muted-foreground hover:text-primary transition-colors duration-300 block font-bold">
                Contact
              </Link>
            </div>
          </div>

          {/* Enhanced Resources */}
          <div>
            <h3 className="font-bold text-foreground mb-6 text-lg">Resources</h3>
            <div className="space-y-3">
              <Link to="/blog" className="text-muted-foreground hover:text-primary transition-colors duration-300 block font-bold">
                Blog
              </Link>
              <Link to="/features" className="text-muted-foreground hover:text-primary transition-colors duration-300 block font-bold">
                Features
              </Link>
              <Link to="/older-adults" className="text-muted-foreground hover:text-primary transition-colors duration-300 block font-bold">
                For Older Adults
              </Link>
              <Link to="/thrivefamily" className="text-muted-foreground hover:text-primary transition-colors duration-300 block font-bold">
                For Families
              </Link>
              <Link to="/family-caregiver-tools" className="text-muted-foreground hover:text-primary transition-colors duration-300 block font-bold">
                Caregiver Tools
              </Link>
              <Link to="/home-safety-quiz" className="text-muted-foreground hover:text-primary transition-colors duration-300 block font-bold">
                Home Safety Check-In
              </Link>
              <Link to="/support/older-adults" className="text-muted-foreground hover:text-primary transition-colors duration-300 block font-bold">
                Support Guides
              </Link>
              <Link to="/professional-network" className="text-muted-foreground hover:text-primary transition-colors duration-300 block font-bold">
                For Professionals
              </Link>
              <Link to="/professionals/directory" className="text-muted-foreground hover:text-primary transition-colors duration-300 block font-bold">
                Find a Professional
              </Link>
              <Link to="/conversation-guide" className="text-muted-foreground hover:text-primary transition-colors duration-300 block font-bold">
                Conversation Guide
              </Link>
              <Link to="/learn" className="text-muted-foreground hover:text-primary transition-colors duration-300 block font-bold">
                Glossary
              </Link>
              <Link to="/checklists" className="text-muted-foreground hover:text-primary transition-colors duration-300 block font-bold">
                Checklists
              </Link>
            </div>
          </div>

          {/* Enhanced Contact & Social */}
          <div>
            <h3 className="font-bold text-foreground mb-6 text-lg">Stay Connected</h3>
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <EnvelopeIcon className="w-4 h-4" />
                  <a href="mailto:support@seniorthrive.com" className="text-sm hover:text-primary transition-colors">support@seniorthrive.com</a>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <PhoneIcon className="w-4 h-4" />
                  <a href="tel:+19895895385" className="text-sm hover:text-primary transition-colors">(989) 589-5385</a>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <MapPinIcon className="w-4 h-4" />
                  <span className="text-sm">Laguna Woods, CA</span>
                </div>
              </div>
              
              {/* Social Media Links */}
              <div className="pt-4">
                <p className="text-sm font-bold text-foreground mb-3">Follow Us</p>
                <div className="flex space-x-3">
                  <a 
                    href="https://www.facebook.com/seniorthrive" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-9 h-9 hover:rounded-full flex items-center justify-center text-primary hover:text-primary/80 transition-all duration-300 hover:-translate-y-0.5"
                    aria-label="Follow us on Facebook"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </a>
                  <a 
                    href="https://www.youtube.com/@SeniorThrive" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-9 h-9 bg-red-500/10 hover:bg-red-500/20 rounded-full flex items-center justify-center text-red-500 hover:text-red-500/80 transition-all duration-300 hover:-translate-y-0.5"
                    aria-label="Subscribe to our YouTube channel"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                    </svg>
                  </a>
                  <a 
                    href="https://www.instagram.com/seniorthrivelife/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-9 h-9 bg-pink-500/10 hover:bg-pink-500/20 rounded-full flex items-center justify-center text-pink-500 hover:text-pink-500/80 transition-all duration-300 hover:-translate-y-0.5"
                    aria-label="Follow us on Instagram"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                  </a>
                  <a 
                    href="https://www.linkedin.com/company/seniorthrive" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-9 h-9 bg-blue-600/10 hover:bg-blue-600/20 rounded-full flex items-center justify-center text-blue-600 hover:text-blue-600/80 transition-all duration-300 hover:-translate-y-0.5"
                    aria-label="Connect with us on LinkedIn"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                  </a>
                  <a 
                    href="https://www.tiktok.com/@seniorthrive" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-9 h-9 bg-slate-800/10 hover:bg-slate-800/20 rounded-full flex items-center justify-center text-slate-800 dark:text-slate-200 hover:text-slate-600 dark:hover:text-slate-400 transition-all duration-300 hover:-translate-y-0.5"
                    aria-label="Follow us on TikTok"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
                    </svg>
                  </a>
                </div>
              </div>
              
              <div className="pt-2">
                <Link to="/contact" className="inline-flex items-center min-h-[48px] px-5 bg-primary text-white font-bold rounded-sm shadow-button-blue hover:shadow-floating hover:bg-black/50 transition-all duration-200">
                  Get in Touch
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-border/50 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
          <div className="flex flex-col items-center md:items-start space-y-2">
            <p className="text-muted-foreground">
              © 2026 SeniorThrive. All rights reserved. Made with ❤️ for older adults everywhere.
            </p>
            <AdminAccessLink />
          </div>
          <div className="flex space-x-6 mt-4 md:mt-0">
            <Link to="/privacy" className="text-muted-foreground hover:text-primary transition-colors duration-300 font-bold">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-muted-foreground hover:text-primary transition-colors duration-300 font-bold">
              Terms of Service
            </Link>
            <Link to="/referral-agreement" className="text-muted-foreground hover:text-primary transition-colors duration-300 font-bold">
              Referral Agreement
            </Link>
            <Link to="/cookies" className="text-muted-foreground hover:text-primary transition-colors duration-300 font-bold">
              Cookie Policy
            </Link>
            <button
              onClick={openSettings}
              className="text-muted-foreground hover:text-primary transition-colors duration-300 font-bold"
            >
              Cookie Settings
            </button>
          </div>
        </div>
      </div>
    </footer>;
};
export default Footer;